/**
 * src/pages/AssistantWorks.tsx
 * アシスタント用: 作品・話数閲覧（閲覧のみ、工程更新なし）
 *
 * ★ モバイル対応:
 *   - 話数一覧: テーブル → カード型
 *   - 横スクロール排除
 */

import { useEffect, useState } from 'react'
import { api, type Work, type Episode } from '../api/client'
import { useIsMobile } from '../hooks/useIsMobile'
import { todayJst } from '../utils/jst'

const STAGE_LABELS: Record<string, string> = {
  name: 'ネーム', name_revision: 'ネーム修正', sketch: '下書き',
  inking: 'ペン入れ', background: '背景', finishing: '仕上げ', submitted: '納品',
}
const STAGES = Object.keys(STAGE_LABELS)

const WORK_TYPE_LABELS: Record<string, string> = {
  story:    'ストーリー',
  tankobon: '単行本',
  color:    'カラー',
}
const WORK_TYPE_BADGE: Record<string, string> = {
  story:    'badge-blue',
  tankobon: 'badge-amber',
  color:    'badge-green',
}

/** 話数ラベル: ストーリーは #番号、単行本・カラーは work_label */
function epLabel(ep: Pick<Episode, 'work_type' | 'episode_number' | 'work_label'>): string {
  if (ep.work_type && ep.work_type !== 'story') return ep.work_label ?? '(ラベルなし)'
  return `#${ep.episode_number}`
}

const DONE_STATUSES = new Set(['completed', 'submitted', 'hiatus'])

// ─── 工程完了チェックユーティリティ ───────────────────────────────
//
// episodes.ts の GET / は GROUP_CONCAT(ps.stage || ':' || ps.status) AS stage_summary
// を返しており、例: "name:done,name_revision:done,sketch:in_progress,..."
//
// 各締切カラムに対応する「完了すべき工程」を個別に確認する。
//   deadline_name_check → name 工程が done
//   deadline_submit     → submitted 工程が done
//   deadline_entry      → submitted 工程が done（入稿 = 最終提出）
// 話数全体が DONE_STATUSES の場合はすべて非表示。

function parseStageMap(ep: any): Record<string, string> {
  const map: Record<string, string> = {}
  const summary = String((ep as any).stage_summary ?? '')
  if (!summary) return map
  for (const pair of summary.split(',')) {
    const idx = pair.indexOf(':')
    if (idx < 0) continue
    const s  = pair.slice(0, idx).trim()
    const st = pair.slice(idx + 1).trim()
    if (s && st) map[s] = st
  }
  return map
}

/** ネーム締切（deadline_name_check）を非表示にするか
 *  話数が DONE_STATUSES、または name 工程が done なら非表示 */
function isNameCheckDone(ep: any): boolean {
  if (DONE_STATUSES.has(ep.status)) return true
  return parseStageMap(ep)['name'] === 'done'
}

/** 提出締切・入稿（deadline_submit / deadline_entry）を非表示にするか
 *  話数が DONE_STATUSES、または submitted 工程が done なら非表示 */
function isSubmitDone(ep: any): boolean {
  if (DONE_STATUSES.has(ep.status)) return true
  return parseStageMap(ep)['submitted'] === 'done'
}

function daysUntil(s: string | null) {
  if (!s) return null
  return Math.ceil((new Date(s + 'T00:00:00').getTime() - new Date(todayJst() + 'T00:00:00').getTime()) / 86400000)
}

function DeadlineChip({ days, status }: { days: number | null; status?: string }) {
  if (status && DONE_STATUSES.has(status)) return <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
  if (days === null) return <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
  if (days < 0)  return <span className="deadline-chip deadline-critical">{Math.abs(days)}日超過</span>
  if (days === 0) return <span className="deadline-chip deadline-critical">今日</span>
  if (days <= 3)  return <span className="deadline-chip deadline-critical">残{days}日</span>
  if (days <= 7)  return <span className="deadline-chip deadline-warning">残{days}日</span>
  return <span className="deadline-chip deadline-ok">残{days}日</span>
}

function StagePips({ summary }: { summary?: string }) {
  if (!summary) return <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>—</span>
  const map: Record<string, string> = {}
  for (const pair of summary.split(',')) {
    const [stage, st] = pair.split(':')
    if (stage && st) map[stage] = st
  }
  return (
    <div className="stage-bar">
      {STAGES.map((s) => (
        <div key={s} className={`stage-pip ${map[s] ?? 'not_started'}`}
          title={`${STAGE_LABELS[s]}: ${map[s] ?? '-'}`} />
      ))}
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    planned: 'badge-gray', in_progress: 'badge-amber',
    submitted: 'badge-blue', completed: 'badge-green', hiatus: 'badge-gray',
  }
  const labels: Record<string, string> = {
    planned: '予定', in_progress: '進行中', submitted: '提出済', completed: '完了', hiatus: '休載',
  }
  return <span className={`badge ${map[status] ?? 'badge-gray'}`}>{labels[status] ?? status}</span>
}

// ★ モバイル用 話数カード
function EpisodeCard({ ep }: { ep: Episode }) {
  const submitDays = daysUntil(ep.deadline_submit)
  // 提出締切の urgent 判定: submitted 工程が完了していれば赤枠にしない
  const submitDone = isSubmitDone(ep)
  const urgent = !submitDone && submitDays !== null && submitDays <= 7

  // 各締切の完了判定
  const nameDone = isNameCheckDone(ep)

  return (
    <div style={{
      background: 'var(--paper)',
      border: `1px solid ${urgent ? 'rgba(192,57,43,0.3)' : 'var(--paper-3)'}`,
      borderRadius: 10,
      padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      {/* 上段: 作品名・話数/ラベル・種別バッジ・ステータス */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 2 }}>{ep.work_title}</div>
          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
            {epLabel(ep)}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <StatusBadge status={ep.status} />
          {ep.work_type && ep.work_type !== 'story' && (
            <span className={`badge ${WORK_TYPE_BADGE[ep.work_type] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>
              {WORK_TYPE_LABELS[ep.work_type] ?? ep.work_type}
            </span>
          )}
        </div>
      </div>

      {/* 工程バー */}
      <div>
        <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}>工程進捗</div>
        <StagePips summary={ep.stage_summary} />
      </div>

      {/* 締切情報: 各締切ごとに対応工程の完了状態を確認 */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {ep.deadline_name_check && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>ネーム</div>
            {/* name 工程が done なら — 表示 */}
            <DeadlineChip days={daysUntil(ep.deadline_name_check)} status={nameDone ? 'completed' : ep.status} />
          </div>
        )}
        {ep.deadline_submit && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>提出締切</div>
            {/* submitted 工程が done なら — 表示 */}
            <DeadlineChip days={submitDays} status={submitDone ? 'completed' : ep.status} />
          </div>
        )}
        {ep.deadline_entry && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 2 }}>入稿</div>
            {/* submitted 工程が done なら — 表示 */}
            <DeadlineChip days={daysUntil(ep.deadline_entry)} status={submitDone ? 'completed' : ep.status} />
          </div>
        )}
      </div>
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
export default function AssistantWorks() {
  const isMobile  = useIsMobile()
  const [works,    setWorks]    = useState<Work[]>([])
  const [episodes, setEpisodes] = useState<Episode[]>([])
  const [selectedWork, setSelectedWork] = useState<string>('all')
  const [loading,  setLoading]  = useState(true)

  const load = async () => {
    const [ws, eps] = await Promise.all([api.works.list(), api.episodes.list()])
    setWorks(ws)
    setEpisodes(eps)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const filteredByWork = selectedWork === 'all' ? episodes : episodes.filter((e) => e.work_id === selectedWork)
  const activeEps      = filteredByWork.filter((ep) => !DONE_STATUSES.has(ep.status))

  if (loading) return <div className="loading">LOADING...</div>

  return (
    <div>
      {/* 作品タブ（モバイルは横スクロール許可） */}
      <div style={{
        display: 'flex', gap: 8, marginBottom: 16,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        overflowX: isMobile ? 'auto' : undefined,
        // スクロールバーを非表示に
        msOverflowStyle: isMobile ? 'none' : undefined,
        scrollbarWidth: isMobile ? 'none' : undefined,
        paddingBottom: isMobile ? 4 : 0,
      } as React.CSSProperties}>
        <button
          className={`btn ${selectedWork === 'all' ? 'btn-primary' : 'btn-outline'}`}
          style={{ flexShrink: 0 }}
          onClick={() => setSelectedWork('all')}
        >
          すべて ({activeEps.length})
        </button>
        {works.map((w) => (
          <button
            key={w.id}
            className={`btn ${selectedWork === w.id ? 'btn-primary' : 'btn-outline'}`}
            style={{ flexShrink: 0 }}
            onClick={() => setSelectedWork(w.id)}
          >
            {w.title}
          </button>
        ))}
      </div>

      {activeEps.length === 0 ? (
        <div className="card">
          <div className="text-muted" style={{ textAlign: 'center', padding: 48 }}>
            進行中の話数はありません
          </div>
        </div>
      ) : isMobile ? (
        // ★ モバイル: カード型
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {activeEps.map((ep) => <EpisodeCard key={ep.id} ep={ep} />)}
        </div>
      ) : (
        // ★ PC: テーブル型（既存のまま）
        <div className="card">
          <div className="card-header">
            <span className="card-title">話数一覧</span>
            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>進行中・予定のみ表示</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>作品</th><th>話数</th><th>ステータス</th><th>工程</th>
                  <th>ネーム締切</th><th>提出締切</th><th>入稿</th>
                </tr>
              </thead>
              <tbody>
                {activeEps.map((ep) => {
                  // 締切ごとの完了判定
                  const nameDone   = isNameCheckDone(ep)  // deadline_name_check
                  const submitDone = isSubmitDone(ep)     // deadline_submit / deadline_entry
                  return (
                  <tr key={ep.id}>
                    <td style={{ fontSize: 12, fontWeight: 500 }}>{ep.work_title}</td>
                    <td className="mono" style={{ fontWeight: 700 }}>{epLabel(ep)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <StatusBadge status={ep.status} />
                        {ep.work_type && ep.work_type !== 'story' && (
                          <span className={`badge ${WORK_TYPE_BADGE[ep.work_type] ?? 'badge-gray'}`} style={{ fontSize: 10 }}>
                            {WORK_TYPE_LABELS[ep.work_type] ?? ep.work_type}
                          </span>
                        )}
                      </div>
                    </td>
                    <td><StagePips summary={ep.stage_summary} /></td>
                    {/* name 工程が done なら — 表示 */}
                    <td><DeadlineChip days={daysUntil(ep.deadline_name_check)} status={nameDone   ? 'completed' : ep.status} /></td>
                    {/* submitted 工程が done なら — 表示 */}
                    <td><DeadlineChip days={daysUntil(ep.deadline_submit)}     status={submitDone ? 'completed' : ep.status} /></td>
                    <td><DeadlineChip days={daysUntil(ep.deadline_entry)}      status={submitDone ? 'completed' : ep.status} /></td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}